import React, { Suspense, useState } from 'react'
import { gql, useQuery, useMutation } from 'urql'
import { useLocation } from 'wouter'
import DetailsPage from '../details/DetailsPage'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Spinner from '../loading/components/Spinner'
import { GenericError } from '../error-pages'
import { ScheduleSelect } from '../selection'
import ScheduleSelectionBlockGrid from './ScheduleSelectionBlockGrid'
import ScheduleSelectionPickDialog from './ScheduleSelectionPickDialog'
import ScheduleSelectionRoundOrders from './ScheduleSelectionRoundOrders'

const query = gql`
  query scheduleSelectionDetails($id: ID!) {
    scheduleSelection(id: $id) {
      id
      name
      status
      rotationLengthDays
      startTime
      timeZone
      numRounds
      pickDeadlineMinutes
      timeoutAction
      currentRound
      currentPickIndex
      currentPickDeadline
      appliedAt
      createdBy {
        id
        name
      }
      targetSchedule {
        id
        name
      }
      participants {
        id
        user {
          id
          name
        }
      }
      blocks {
        id
        blockIndex
        startTime
        endTime
        pickedBy {
          id
          user {
            id
            name
          }
        }
        pickedAt
      }
      currentPicker {
        id
        user {
          id
          name
        }
      }
      roundOrders {
        participant {
          id
          user {
            id
            name
          }
        }
        pickPosition
      }
    }
  }
`

const startMutation = gql`
  mutation startScheduleSelection($id: ID!) {
    startScheduleSelection(id: $id)
  }
`

const cancelMutation = gql`
  mutation cancelScheduleSelection($id: ID!) {
    cancelScheduleSelection(id: $id)
  }
`

const applyMutation = gql`
  mutation applyScheduleSelection($id: ID!, $targetScheduleID: ID) {
    applyScheduleSelection(id: $id, targetScheduleID: $targetScheduleID)
  }
`

const deleteMutation = gql`
  mutation deleteScheduleSelection($id: ID!) {
    deleteScheduleSelection(id: $id)
  }
`

function statusLabel(status: string): string {
  switch (status) {
    case 'setup':
      return 'Setup'
    case 'in_progress':
      return 'In Progress'
    case 'completed':
      return 'Completed'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status
  }
}

function statusColor(
  status: string,
): 'default' | 'primary' | 'success' | 'warning' {
  switch (status) {
    case 'in_progress':
      return 'primary'
    case 'completed':
      return 'success'
    case 'cancelled':
      return 'warning'
    default:
      return 'default'
  }
}

interface ScheduleSelectionDetailsProps {
  id: string
}

const queryContext = { suspense: false }

export default function ScheduleSelectionDetails(
  props: ScheduleSelectionDetailsProps,
): React.JSX.Element {
  const id = props.id
  const [showPick, setShowPick] = useState(false)
  const [showApplyDialog, setShowApplyDialog] = useState(false)
  const [applyTargetScheduleID, setApplyTargetScheduleID] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const [, navigate] = useLocation()
  const [q] = useQuery({
    query,
    variables: { id },
    pause: !id,
    context: queryContext,
  })
  const [, startSel] = useMutation(startMutation)
  const [, cancelSel] = useMutation(cancelMutation)
  const [, applySel] = useMutation(applyMutation)
  const [, deleteSel] = useMutation(deleteMutation)

  if (!id) {
    return <GenericError error='No schedule selection ID provided.' />
  }

  if (q.fetching) {
    return <Spinner />
  }

  if (q.error) {
    return (
      <Alert severity='error'>
        {q.error.message || 'Failed to load schedule selection.'}
      </Alert>
    )
  }

  const sel = q.data?.scheduleSelection
  if (!sel) {
    return <GenericError error='Schedule selection not found.' />
  }

  const isCurrentUserPicker =
    sel.status === 'in_progress' && !!sel.currentPicker?.user

  const secondaryActions = []
  if (sel.status === 'setup') {
    secondaryActions.push({
      label: actionLoading ? 'Starting...' : 'Start Selection',
      handleOnClick: () => {
        setActionLoading(true)
        startSel({ id }).then((res) => {
          if (res.error) setActionLoading(false)
        })
      },
      ButtonProps: { disabled: actionLoading },
    })
  }
  if (sel.status === 'setup' || sel.status === 'in_progress') {
    secondaryActions.push({
      label: 'Cancel',
      handleOnClick: () => {
        setActionLoading(true)
        cancelSel({ id }).then((res) => {
          if (res.error) setActionLoading(false)
        })
      },
      ButtonProps: { disabled: actionLoading },
    })
  }
  if (sel.status !== 'in_progress') {
    secondaryActions.push({
      label: 'Delete',
      handleOnClick: () => {
        if (window.confirm('Are you sure you want to delete this schedule selection?')) {
          setActionLoading(true)
          deleteSel({ id }).then((res) => {
            if (res.error) setActionLoading(false)
            else navigate('/schedule-selections')
          })
        }
      },
      ButtonProps: { disabled: actionLoading },
    })
  }
  if (sel.status === 'completed' && !sel.appliedAt) {
    secondaryActions.push({
      label: actionLoading ? 'Applying...' : 'Apply to Schedule',
      handleOnClick: () => {
        if (sel.targetSchedule) {
          setActionLoading(true)
          applySel({ id }).then((res) => {
            if (res.error) setActionLoading(false)
          })
        } else {
          setShowApplyDialog(true)
        }
      },
      ButtonProps: { disabled: actionLoading },
    })
  }

  return (
    <React.Fragment>
      <Suspense>
        {showPick && (
          <ScheduleSelectionPickDialog
            selectionID={id}
            onClose={() => setShowPick(false)}
          />
        )}
      </Suspense>
      <Dialog
        open={showApplyDialog}
        onClose={() => setShowApplyDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Apply to Schedule</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Select the schedule to apply this selection to.
          </Typography>
          <ScheduleSelect
            value={applyTargetScheduleID}
            onChange={(val: string) => setApplyTargetScheduleID(val)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApplyDialog(false)}>Cancel</Button>
          <Button
            variant='contained'
            disabled={!applyTargetScheduleID || mutating}
            onClick={() => {
              applySel({ id, targetScheduleID: applyTargetScheduleID }).then((res) => {
                if (!res.error) setShowApplyDialog(false)
              })
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      <DetailsPage
        title={sel.name}
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size='small'
              label={statusLabel(sel.status)}
              color={statusColor(sel.status)}
            />
            <Typography variant='body2'>
              {sel.rotationLengthDays}-day blocks | {sel.numRounds} rounds |{' '}
              {sel.participants.length} participants
            </Typography>
          </Box>
        }
        details={
          sel.targetSchedule
            ? `Target: ${sel.targetSchedule.name}` +
              (sel.appliedAt ? ' (Applied)' : '')
            : 'No target schedule set'
        }
        secondaryActions={secondaryActions}
        pageContent={
          <Box sx={{ p: 2 }}>
            {isCurrentUserPicker && (
              <Alert
                severity='info'
                sx={{ mb: 2 }}
                action={
                  <Button
                    color='inherit'
                    size='small'
                    onClick={() => setShowPick(true)}
                  >
                    Pick Now
                  </Button>
                }
              >
                It&apos;s {sel.currentPicker?.user?.name}&apos;s turn to pick!
                {sel.currentPickDeadline &&
                  ` Deadline: ${new Date(sel.currentPickDeadline).toLocaleString()}`}
              </Alert>
            )}
            {sel.blocks.length > 0 && (
              <React.Fragment>
                <Typography variant='subtitle1' color='textSecondary' sx={{ mb: 1 }}>
                  Blocks
                </Typography>
                <ScheduleSelectionBlockGrid
                  blocks={sel.blocks}
                  participants={sel.participants}
                  timeZone={sel.timeZone}
                />
              </React.Fragment>
            )}
            {sel.roundOrders && sel.roundOrders.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant='subtitle1' color='textSecondary' sx={{ mb: 1 }}>
                  Pick Order by Round
                </Typography>
                <ScheduleSelectionRoundOrders roundOrders={sel.roundOrders} />
              </Box>
            )}
          </Box>
        }
      />
    </React.Fragment>
  )
}
